/**
 * Pure input-sanitiser helpers shared by the numeric input components
 * (PostalInput / PhoneInput / NumericInput). Kept dep-free and DOM-free so
 * they unit-test under the node vitest environment — the components are thin
 * wrappers that set `inputMode` (the real mobile-keyboard win) and route
 * onChange through these.
 */

/** SG postal codes are 6 digits — digits only, hard-capped at 6. */
export function sanitizePostal(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, 6);
}

/**
 * Phone numbers: digits + spaces, plus a single leading `+` for the
 * international country code (foreign WhatsApp numbers). Any `+` that is not
 * the first character is dropped; letters/punctuation are stripped.
 */
export function sanitizePhone(raw: string): string {
  const cleaned = raw.replace(/[^\d+\s]/g, '');
  // Keep a `+` only if it is the very first character.
  const leadingPlus = cleaned.startsWith('+') ? '+' : '';
  return leadingPlus + cleaned.replace(/\+/g, '');
}

/**
 * Generic numeric input. Integer mode → digits only. Decimal mode → digits
 * plus a single dot (a leading dot is allowed; extra dots are collapsed).
 */
export function sanitizeNumeric(raw: string, decimal: boolean): string {
  if (!decimal) return raw.replace(/\D/g, '');
  const cleaned = raw.replace(/[^\d.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  // Keep the first dot, remove any subsequent dots.
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
}
